/**
 * Component-Specific Visual Feedback Patterns
 * Specialized feedback for Charts, Containers, Layout, Text, and Custom components
 */

import {
  ChartComponentFeedback,
  ContainerComponentFeedback,
  LayoutComponentFeedback,
  TextComponentFeedback,
  CustomComponentFeedback,
  VisualFeedbackContext,
  VisualFeedbackState,
} from "./types";
import { ComponentType } from "@/types";
import { HardwareAcceleratedAnimationFramework } from "./HardwareAcceleratedAnimationFramework";

/**
 * Manager for component-specific visual feedback patterns
 */
export class ComponentSpecificFeedbackPatterns {
  private animationFramework: HardwareAcceleratedAnimationFramework;
  private chartPatterns: ChartFeedbackHandler;
  private containerPatterns: ContainerFeedbackHandler;
  private layoutPatterns: LayoutFeedbackHandler;
  private textPatterns: TextFeedbackHandler;
  private customPatterns: Map<string, CustomFeedbackHandler> = new Map();

  constructor(animationFramework: HardwareAcceleratedAnimationFramework) {
    this.animationFramework = animationFramework;
    this.initializePatternHandlers();
  }

  /**
   * Initialize component-specific pattern handlers
   */
  private initializePatternHandlers(): void {
    this.chartPatterns = new ChartFeedbackHandler(this.animationFramework);
    this.containerPatterns = new ContainerFeedbackHandler(
      this.animationFramework,
    );
    this.layoutPatterns = new LayoutFeedbackHandler(this.animationFramework);
    this.textPatterns = new TextFeedbackHandler(this.animationFramework);
  }

  /**
   * Apply component-specific feedback based on component type
   */
  public async applyComponentFeedback(
    componentType: ComponentType,
    state: VisualFeedbackState,
    context: VisualFeedbackContext,
    element: HTMLElement,
  ): Promise<void> {
    switch (componentType) {
      case ComponentType.CHART:
        await this.chartPatterns.applyFeedback(state, context, element);
        break;
      case ComponentType.CONTAINER:
        await this.containerPatterns.applyFeedback(state, context, element);
        break;
      case ComponentType.LAYOUT:
        await this.layoutPatterns.applyFeedback(state, context, element);
        break;
      case ComponentType.TEXT:
        await this.textPatterns.applyFeedback(state, context, element);
        break;
      default: {
        const customHandler = this.customPatterns.get(componentType);
        if (customHandler) {
          await customHandler.applyFeedback(state, context, element);
        } else {
          // Apply default feedback for unknown types
          await this.applyDefaultFeedback(state, context, element);
        }
      }
    }
  }

  /**
   * Register custom feedback pattern for specific component type
   */
  public registerCustomPattern(
    componentType: string,
    pattern: CustomComponentFeedback,
  ): void {
    const handler = new CustomFeedbackHandler(this.animationFramework, pattern);
    this.customPatterns.set(componentType, handler);
  }

  /**
   * Apply default feedback for components without specific patterns
   */
  private async applyDefaultFeedback(
    state: VisualFeedbackState,
    context: VisualFeedbackContext,
    element: HTMLElement,
  ): Promise<void> {
    // Apply basic visual feedback without component-specific enhancements
    element.classList.add(`terra-component-${state}`);
  }

  /**
   * Remove component-specific feedback
   */
  public removeComponentFeedback(
    componentType: ComponentType,
    element: HTMLElement,
  ): void {
    // Remove all component-specific classes
    const feedbackClasses = [
      "terra-chart-feedback",
      "terra-container-feedback",
      "terra-layout-feedback",
      "terra-text-feedback",
      "terra-custom-feedback",
    ];

    feedbackClasses.forEach((cls) => element.classList.remove(cls));

    // Remove component-specific elements
    element
      .querySelectorAll(
        ".terra-data-indicator, .terra-mini-viz, .terra-drop-zone-indicator, " +
          ".terra-alignment-guide, .terra-spacing-indicator, .terra-editing-indicator",
      )
      .forEach((el) => el.remove());
  }
}

/**
 * Chart Component Feedback Handler
 * Data connection indicators, mini visualizations, data binding feedback
 */
class ChartFeedbackHandler {
  private config: ChartComponentFeedback;

  constructor(
    private animationFramework: HardwareAcceleratedAnimationFramework,
  ) {
    this.config = {
      dataConnectionIndicator: {
        enabled: true,
        position: "bottom-center",
        color: "hsl(220, 100%, 60%)",
        animationType: "pulse",
      },
      miniVisualization: {
        enabled: true,
        previewType: "bars",
        opacity: 0.6,
        animateOnHover: true,
      },
      dataBinding: {
        enabled: true,
        glowEffect: true,
        connectionLines: true,
      },
    };
  }

  public async applyFeedback(
    state: VisualFeedbackState,
    context: VisualFeedbackContext,
    element: HTMLElement,
  ): Promise<void> {
    element.classList.add("terra-chart-feedback");

    switch (state) {
      case VisualFeedbackState.READY:
        await this.applyReadyStateFeedback(element);
        break;
      case VisualFeedbackState.ACTIVE:
        await this.applyActiveStateFeedback(element);
        break;
      case VisualFeedbackState.SUCCESS:
        await this.applySuccessStateFeedback(element);
        break;
    }
  }

  private async applyReadyStateFeedback(element: HTMLElement): Promise<void> {
    if (this.config.dataConnectionIndicator.enabled) {
      this.createDataConnectionIndicator(element);
    }

    if (this.config.miniVisualization.enabled) {
      this.createMiniVisualization(element);
    }
  }

  private async applyActiveStateFeedback(element: HTMLElement): Promise<void> {
    if (this.config.dataBinding.enabled) {
      this.showDataBindingEffects(element);
    }
  }

  private async applySuccessStateFeedback(element: HTMLElement): Promise<void> {
    // Animate mini visualization to full size
    const miniViz = element.querySelector(".terra-mini-viz") as HTMLElement;
    if (miniViz) {
      await this.animateToFullVisualization(miniViz);
    }
  }

  private createDataConnectionIndicator(element: HTMLElement): void {
    const indicator = document.createElement("div");
    indicator.className = "terra-data-indicator";
    indicator.style.cssText = `
      position: absolute;
      ${this.getPositionStyles(this.config.dataConnectionIndicator.position)}
      width: 12px;
      height: 12px;
      background: ${this.config.dataConnectionIndicator.color};
      border-radius: 50%;
      animation: data-pulse 1.5s ease-in-out infinite;
    `;

    element.appendChild(indicator);

    // Add CSS animation
    if (!document.getElementById("chart-feedback-styles")) {
      this.addChartFeedbackStyles();
    }
  }

  private createMiniVisualization(element: HTMLElement): void {
    const miniViz = document.createElement("div");
    miniViz.className = "terra-mini-viz";
    miniViz.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 60px;
      height: 40px;
      opacity: ${this.config.miniVisualization.opacity};
      pointer-events: none;
    `;

    // Create visualization based on type
    const vizContent = this.createVisualizationContent(
      this.config.miniVisualization.previewType,
    );
    miniViz.appendChild(vizContent);

    element.appendChild(miniViz);

    // Add hover animation if enabled
    if (this.config.miniVisualization.animateOnHover) {
      element.addEventListener("mouseenter", () => {
        miniViz.style.transform = "translate(-50%, -50%) scale(1.1)";
      });
      element.addEventListener("mouseleave", () => {
        miniViz.style.transform = "translate(-50%, -50%) scale(1)";
      });
    }
  }

  private showDataBindingEffects(element: HTMLElement): void {
    if (this.config.dataBinding.glowEffect) {
      element.style.boxShadow = "0 0 20px rgba(34, 139, 230, 0.4)";
    }

    if (this.config.dataBinding.connectionLines) {
      this.createConnectionLines(element);
    }
  }

  private createConnectionLines(element: HTMLElement): void {
    // Create animated connection lines to data source
    const lines = document.createElement("div");
    lines.className = "terra-connection-lines";
    lines.innerHTML = `
      <div class="connection-line"></div>
      <div class="connection-line"></div>
      <div class="connection-line"></div>
    `;
    element.appendChild(lines);
  }

  private async animateToFullVisualization(
    miniViz: HTMLElement,
  ): Promise<void> {
    await this.animationFramework.createAnimation(
      miniViz,
      {
        transform: "translate(-50%, -50%) scale(1.5)",
        opacity: 1,
        willChange: ["transform", "opacity"],
        backfaceVisibility: "hidden",
        perspective: 1000,
      },
      {
        duration: 400,
        easing: "ease-out",
        delay: 0,
        iterations: 1,
        direction: "normal",
        fillMode: "forwards",
      },
    );
  }

  private createVisualizationContent(
    type: "bars" | "line" | "pie" | "scatter",
  ): HTMLElement {
    const content = document.createElement("div");
    content.style.cssText = `
      width: 100%;
      height: 100%;
      display: flex;
      align-items: end;
      justify-content: space-around;
    `;

    switch (type) {
      case "bars":
        // Create mini bar chart
        for (let i = 0; i < 4; i++) {
          const bar = document.createElement("div");
          bar.style.cssText = `
            width: 8px;
            height: ${20 + Math.random() * 20}px;
            background: linear-gradient(to top, #3b82f6, #60a5fa);
            border-radius: 1px;
            animation: bar-grow 0.5s ease-out ${i * 0.1}s both;
          `;
          content.appendChild(bar);
        }
        break;
      case "line":
        // Create mini line chart
        content.innerHTML = `
          <svg width="60" height="40" viewBox="0 0 60 40">
            <path d="M5,30 Q15,10 25,20 T45,15"
                  stroke="#3b82f6"
                  stroke-width="2"
                  fill="none"
                  stroke-dasharray="100"
                  style="animation: line-draw 1s ease-out forwards;" />
          </svg>
        `;
        break;
      case "pie":
        // Create mini pie chart
        content.innerHTML = `
          <svg width="40" height="40" viewBox="0 0 40 40">
            <circle cx="20" cy="20" r="15" fill="none" stroke="#e5e7eb" stroke-width="3"/>
            <circle cx="20" cy="20" r="15" fill="none" stroke="#3b82f6" stroke-width="3"
                    stroke-dasharray="60" stroke-dashoffset="0"
                    style="animation: pie-fill 1s ease-out forwards;" />
          </svg>
        `;
        break;
      case "scatter":
        // Create mini scatter plot
        for (let i = 0; i < 8; i++) {
          const dot = document.createElement("div");
          dot.style.cssText = `
            position: absolute;
            width: 3px;
            height: 3px;
            background: #3b82f6;
            border-radius: 50%;
            left: ${10 + Math.random() * 40}px;
            top: ${10 + Math.random() * 20}px;
            animation: dot-appear 0.3s ease-out ${i * 0.05}s both;
          `;
          content.appendChild(dot);
        }
        break;
    }

    return content;
  }

  private getPositionStyles(position: string): string {
    switch (position) {
      case "bottom-center":
        return "bottom: -6px; left: 50%; transform: translateX(-50%);";
      case "top-left":
        return "top: -6px; left: -6px;";
      case "top-right":
        return "top: -6px; right: -6px;";
      default:
        return "bottom: -6px; left: 50%; transform: translateX(-50%);";
    }
  }

  private addChartFeedbackStyles(): void {
    const style = document.createElement("style");
    style.id = "chart-feedback-styles";
    style.textContent = `
      @keyframes data-pulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.6; transform: scale(1.2); }
      }

      @keyframes bar-grow {
        from { height: 2px; }
        to { height: var(--final-height); }
      }

      @keyframes line-draw {
        from { stroke-dashoffset: 100; }
        to { stroke-dashoffset: 0; }
      }

      @keyframes pie-fill {
        from { stroke-dashoffset: 60; }
        to { stroke-dashoffset: 0; }
      }

      @keyframes dot-appear {
        from { opacity: 0; transform: scale(0); }
        to { opacity: 1; transform: scale(1); }
      }

      .terra-connection-lines {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
      }

      .connection-line {
        position: absolute;
        width: 2px;
        height: 20px;
        background: linear-gradient(to bottom, transparent, #3b82f6);
        animation: connection-pulse 2s ease-in-out infinite;
      }

      .connection-line:nth-child(1) {
        top: 10%;
        left: 20%;
        animation-delay: 0s;
      }

      .connection-line:nth-child(2) {
        top: 30%;
        right: 25%;
        animation-delay: 0.3s;
      }

      .connection-line:nth-child(3) {
        bottom: 15%;
        left: 50%;
        animation-delay: 0.6s;
      }

      @keyframes connection-pulse {
        0%, 100% { opacity: 0.3; }
        50% { opacity: 0.8; }
      }
    `;
    document.head.appendChild(style);
  }
}

/**
 * Container Component Feedback Handler
 * Drop zone highlighting, containment indicators, child alignment
 */
class ContainerFeedbackHandler {
  private config: ContainerComponentFeedback;

  constructor(
    private animationFramework: HardwareAcceleratedAnimationFramework,
  ) {
    this.config = {
      dropZoneHighlight: {
        enabled: true,
        borderStyle: "dashed",
        borderColor: "hsl(var(--primary))",
        borderOpacity: 0.6,
      },
      containmentIndicator: {
        enabled: true,
        message: "Drop components here",
        messagePosition: "center",
        backgroundColor: "hsl(var(--muted))",
        backgroundOpacity: 0.1,
      },
      childAlignment: {
        enabled: true,
        alignmentGuides: true,
        spacingIndicators: true,
      },
    };
  }

  public async applyFeedback(
    state: VisualFeedbackState,
    context: VisualFeedbackContext,
    element: HTMLElement,
  ): Promise<void> {
    element.classList.add("terra-container-feedback");

    switch (state) {
      case VisualFeedbackState.READY:
        await this.applyReadyStateFeedback(element);
        break;
      case VisualFeedbackState.ACTIVE:
        await this.applyActiveStateFeedback(element);
        break;
    }
  }

  private async applyReadyStateFeedback(element: HTMLElement): Promise<void> {
    if (this.config.dropZoneHighlight.enabled) {
      this.highlightDropZone(element);
    }

    if (this.config.containmentIndicator.enabled) {
      this.showContainmentIndicator(element);
    }
  }

  private async applyActiveStateFeedback(element: HTMLElement): Promise<void> {
    if (this.config.childAlignment.enabled) {
      this.showChildAlignmentGuides(element);
    }
  }

  private highlightDropZone(element: HTMLElement): void {
    const config = this.config.dropZoneHighlight;
    element.style.cssText += `
      border: 2px ${config.borderStyle} ${config.borderColor};
      border-opacity: ${config.borderOpacity};
      background: ${this.config.containmentIndicator.backgroundColor};
      background-opacity: ${this.config.containmentIndicator.backgroundOpacity};
    `;
  }

  private showContainmentIndicator(element: HTMLElement): void {
    const indicator = document.createElement("div");
    indicator.className = "terra-drop-zone-indicator";
    indicator.textContent = this.config.containmentIndicator.message;
    indicator.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: hsl(var(--muted-foreground));
      font-size: 0.875rem;
      font-weight: 500;
      pointer-events: none;
      opacity: 0.7;
    `;

    element.appendChild(indicator);
  }

  private showChildAlignmentGuides(element: HTMLElement): void {
    if (this.config.childAlignment.alignmentGuides) {
      this.createAlignmentGuides(element);
    }

    if (this.config.childAlignment.spacingIndicators) {
      this.createSpacingIndicators(element);
    }
  }

  private createAlignmentGuides(element: HTMLElement): void {
    // Create center guides
    const vGuide = document.createElement("div");
    vGuide.className = "terra-alignment-guide vertical-center";
    vGuide.style.cssText = `
      position: absolute;
      left: 50%;
      top: 0;
      width: 1px;
      height: 100%;
      background: hsl(var(--primary));
      opacity: 0.4;
      transform: translateX(-50%);
    `;

    const hGuide = document.createElement("div");
    hGuide.className = "terra-alignment-guide horizontal-center";
    hGuide.style.cssText = `
      position: absolute;
      top: 50%;
      left: 0;
      height: 1px;
      width: 100%;
      background: hsl(var(--primary));
      opacity: 0.4;
      transform: translateY(-50%);
    `;

    element.appendChild(vGuide);
    element.appendChild(hGuide);
  }

  private createSpacingIndicators(element: HTMLElement): void {
    const spacingGrid = document.createElement("div");
    spacingGrid.className = "terra-spacing-indicators";
    spacingGrid.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-image:
        linear-gradient(to right, hsl(var(--muted)) 1px, transparent 1px),
        linear-gradient(to bottom, hsl(var(--muted)) 1px, transparent 1px);
      background-size: 20px 20px;
      opacity: 0.2;
      pointer-events: none;
    `;

    element.appendChild(spacingGrid);
  }
}

/**
 * Layout Component Feedback Handler
 * Alignment guides, spacing indicators, grid overlay
 */
class LayoutFeedbackHandler {
  private config: LayoutComponentFeedback;

  constructor(
    private animationFramework: HardwareAcceleratedAnimationFramework,
  ) {
    this.config = {
      alignmentGuides: {
        enabled: true,
        color: "hsl(var(--primary))",
        thickness: 1,
        magneticSnap: true,
        snapDistance: 16,
      },
      spacingIndicators: {
        enabled: true,
        color: "hsl(var(--muted-foreground))",
        style: "measurement",
        showValues: true,
      },
      gridOverlay: {
        enabled: true,
        gridSize: 20,
        opacity: 0.1,
        color: "hsl(var(--border))",
      },
    };
  }

  public async applyFeedback(
    state: VisualFeedbackState,
    context: VisualFeedbackContext,
    element: HTMLElement,
  ): Promise<void> {
    element.classList.add("terra-layout-feedback");

    switch (state) {
      case VisualFeedbackState.READY:
        await this.applyReadyStateFeedback(element);
        break;
      case VisualFeedbackState.ACTIVE:
        await this.applyActiveStateFeedback(element);
        break;
    }
  }

  private async applyReadyStateFeedback(element: HTMLElement): Promise<void> {
    if (this.config.gridOverlay.enabled) {
      this.showGridOverlay(element);
    }
  }

  private async applyActiveStateFeedback(element: HTMLElement): Promise<void> {
    if (this.config.alignmentGuides.enabled) {
      this.showAlignmentGuides(element);
    }

    if (this.config.spacingIndicators.enabled) {
      this.showSpacingIndicators(element);
    }
  }

  private showGridOverlay(element: HTMLElement): void {
    const config = this.config.gridOverlay;
    element.style.backgroundImage = `
      linear-gradient(to right, ${config.color} 1px, transparent 1px),
      linear-gradient(to bottom, ${config.color} 1px, transparent 1px)
    `;
    element.style.backgroundSize = `${config.gridSize}px ${config.gridSize}px`;
    element.style.backgroundPosition = "0 0, 0 0";
  }

  private showAlignmentGuides(element: HTMLElement): void {
    // Dynamic alignment guides will be created based on nearby components
    const guidesContainer = document.createElement("div");
    guidesContainer.className = "terra-alignment-guides-container";
    guidesContainer.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 1000;
    `;

    element.appendChild(guidesContainer);
  }

  private showSpacingIndicators(element: HTMLElement): void {
    const indicators = document.createElement("div");
    indicators.className = "terra-spacing-indicators";
    indicators.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
    `;

    // Add measurement lines and values based on style
    if (this.config.spacingIndicators.style === "measurement") {
      this.createMeasurementIndicators(indicators);
    }

    element.appendChild(indicators);
  }

  private createMeasurementIndicators(container: HTMLElement): void {
    // Create measurement lines with values
    const measurements = [
      { type: "horizontal", position: "25%", value: "100px" },
      { type: "vertical", position: "25%", value: "80px" },
      { type: "horizontal", position: "75%", value: "150px" },
      { type: "vertical", position: "75%", value: "120px" },
    ];

    measurements.forEach((measurement) => {
      const line = document.createElement("div");
      line.className = `measurement-line ${measurement.type}`;
      line.style.cssText = `
        position: absolute;
        background: ${this.config.spacingIndicators.color};
        opacity: 0.6;
        ${
          measurement.type === "horizontal"
            ? `height: 1px; width: 40px; top: ${measurement.position}; left: 10px;`
            : `width: 1px; height: 40px; left: ${measurement.position}; top: 10px;`
        }
      `;

      if (this.config.spacingIndicators.showValues) {
        const label = document.createElement("span");
        label.textContent = measurement.value;
        label.style.cssText = `
          position: absolute;
          font-size: 0.75rem;
          color: ${this.config.spacingIndicators.color};
          ${
            measurement.type === "horizontal"
              ? "top: -20px; left: 50%; transform: translateX(-50%);"
              : "left: -30px; top: 50%; transform: translateY(-50%);"
          }
        `;
        line.appendChild(label);
      }

      container.appendChild(line);
    });
  }
}

/**
 * Text Component Feedback Handler
 * Editing states, formatting indicators, text flow visualization
 */
class TextFeedbackHandler {
  private config: TextComponentFeedback;

  constructor(
    private animationFramework: HardwareAcceleratedAnimationFramework,
  ) {
    this.config = {
      editingState: {
        enabled: true,
        borderColor: "hsl(var(--primary))",
        borderStyle: "2px solid",
        caretAnimation: true,
      },
      formattingIndicators: {
        enabled: true,
        toolbarPosition: "above",
        showOnHover: true,
        animationDuration: 200,
      },
      textFlow: {
        enabled: true,
        overflowIndicator: true,
        lineHeightGuides: true,
      },
    };
  }

  public async applyFeedback(
    state: VisualFeedbackState,
    context: VisualFeedbackContext,
    element: HTMLElement,
  ): Promise<void> {
    element.classList.add("terra-text-feedback");

    switch (state) {
      case VisualFeedbackState.READY:
        await this.applyReadyStateFeedback(element);
        break;
      case VisualFeedbackState.ACTIVE:
        await this.applyActiveStateFeedback(element);
        break;
    }
  }

  private async applyReadyStateFeedback(element: HTMLElement): Promise<void> {
    if (this.config.formattingIndicators.enabled) {
      this.showFormattingIndicators(element);
    }
  }

  private async applyActiveStateFeedback(element: HTMLElement): Promise<void> {
    if (this.config.editingState.enabled) {
      this.showEditingState(element);
    }

    if (this.config.textFlow.enabled) {
      this.showTextFlowIndicators(element);
    }
  }

  private showFormattingIndicators(element: HTMLElement): void {
    const toolbar = document.createElement("div");
    toolbar.className = "terra-formatting-toolbar";
    toolbar.style.cssText = `
      position: absolute;
      ${this.getToolbarPosition()}
      background: hsl(var(--background));
      border: 1px solid hsl(var(--border));
      border-radius: 0.375rem;
      padding: 0.25rem;
      display: flex;
      gap: 0.25rem;
      opacity: 0;
      transform: translateY(-5px);
      transition: all ${this.config.formattingIndicators.animationDuration}ms ease;
      z-index: 1001;
    `;

    // Add formatting buttons
    const buttons = ["B", "I", "U", "A"];
    buttons.forEach((button) => {
      const btn = document.createElement("button");
      btn.textContent = button;
      btn.style.cssText = `
        width: 24px;
        height: 24px;
        border: none;
        border-radius: 0.25rem;
        background: transparent;
        cursor: pointer;
        font-weight: bold;
      `;
      toolbar.appendChild(btn);
    });

    element.appendChild(toolbar);

    if (this.config.formattingIndicators.showOnHover) {
      element.addEventListener("mouseenter", () => {
        toolbar.style.opacity = "1";
        toolbar.style.transform = "translateY(0)";
      });

      element.addEventListener("mouseleave", () => {
        toolbar.style.opacity = "0";
        toolbar.style.transform = "translateY(-5px)";
      });
    } else {
      toolbar.style.opacity = "1";
      toolbar.style.transform = "translateY(0)";
    }
  }

  private showEditingState(element: HTMLElement): void {
    const config = this.config.editingState;
    element.style.border = config.borderStyle + " " + config.borderColor;
    element.style.outline = "none";

    if (config.caretAnimation) {
      const caret = document.createElement("div");
      caret.className = "terra-text-caret";
      caret.style.cssText = `
        position: absolute;
        right: 4px;
        top: 50%;
        transform: translateY(-50%);
        width: 1px;
        height: 1em;
        background: ${config.borderColor};
        animation: caret-blink 1s infinite;
      `;

      element.appendChild(caret);

      // Add caret animation CSS if not present
      if (!document.getElementById("text-caret-styles")) {
        const style = document.createElement("style");
        style.id = "text-caret-styles";
        style.textContent = `
          @keyframes caret-blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0; }
          }
        `;
        document.head.appendChild(style);
      }
    }
  }

  private showTextFlowIndicators(element: HTMLElement): void {
    if (this.config.textFlow.overflowIndicator) {
      this.createOverflowIndicator(element);
    }

    if (this.config.textFlow.lineHeightGuides) {
      this.createLineHeightGuides(element);
    }
  }

  private createOverflowIndicator(element: HTMLElement): void {
    const indicator = document.createElement("div");
    indicator.className = "terra-overflow-indicator";
    indicator.innerHTML = "⋯";
    indicator.style.cssText = `
      position: absolute;
      bottom: 0;
      right: 0;
      background: hsl(var(--destructive));
      color: white;
      font-size: 0.75rem;
      padding: 2px 4px;
      border-radius: 0.25rem;
      opacity: 0.8;
    `;

    element.appendChild(indicator);
  }

  private createLineHeightGuides(element: HTMLElement): void {
    const guides = document.createElement("div");
    guides.className = "terra-line-guides";
    guides.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-image: repeating-linear-gradient(
        to bottom,
        transparent,
        transparent 1.4em,
        hsl(var(--muted)) 1.4em,
        hsl(var(--muted)) calc(1.4em + 1px)
      );
      opacity: 0.3;
      pointer-events: none;
    `;

    element.appendChild(guides);
  }

  private getToolbarPosition(): string {
    switch (this.config.formattingIndicators.toolbarPosition) {
      case "above":
        return "bottom: 100%; left: 0; margin-bottom: 0.25rem;";
      case "below":
        return "top: 100%; left: 0; margin-top: 0.25rem;";
      case "floating":
        return "top: -40px; left: 50%; transform: translateX(-50%);";
      default:
        return "bottom: 100%; left: 0; margin-bottom: 0.25rem;";
    }
  }
}

/**
 * Custom Component Feedback Handler
 * Extensible feedback system for custom component types
 */
class CustomFeedbackHandler {
  constructor(
    private animationFramework: HardwareAcceleratedAnimationFramework,
    private config: CustomComponentFeedback,
  ) {}

  public async applyFeedback(
    state: VisualFeedbackState,
    context: VisualFeedbackContext,
    element: HTMLElement,
  ): Promise<void> {
    element.classList.add("terra-custom-feedback");

    // Apply custom animations if defined
    const stateAnimations = this.config.customAnimations.get(state);
    if (stateAnimations) {
      await this.applyCustomAnimations(element, stateAnimations);
    }

    // Apply custom states if defined
    const customState = this.config.customStates.get(state);
    if (customState) {
      this.applyCustomState(element, customState);
    }

    // Execute extension points
    if (this.config.extensionPoints.beforeStateChange) {
      this.config.extensionPoints.beforeStateChange(
        context.currentState,
        state,
      );
    }
  }

  private async applyCustomAnimations(
    element: HTMLElement,
    animationConfig: any,
  ): Promise<void> {
    // Apply custom animation configuration
    await this.animationFramework.createAnimation(
      element,
      animationConfig.properties,
      animationConfig.timing,
    );
  }

  private applyCustomState(element: HTMLElement, visualState: any): void {
    // Apply custom visual state styles
    if (visualState.styles) {
      Object.assign(element.style, visualState.styles);
    }

    // Add custom CSS classes
    if (visualState.cssClasses) {
      visualState.cssClasses.forEach((cls: string) =>
        element.classList.add(cls),
      );
    }
  }
}
